import { ChangeEvent, FC, useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { DataContext } from "@/provider/DataContextProvider";
import { useSetDeviceByUuid, useSetEntryCustomData } from "@/services/api";
import { SIM_ALIAS_MAX_LENGTH } from "@/utils/constants";
import {
  getEntry,
  isValidSimAlias,
  withPreservedSimAlias,
  withSimAlias,
} from "@/utils/dataContextHelpers";
import { getDefaultAliasKey, getDeviceForProduct } from "@/utils/productMatrix";

type AliasForm = { alias: string };

interface Props {
  showErrors?: boolean;
}

export const DeviceDetailsForm: FC<Props> = ({ showErrors }) => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const { t } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.checkout.summary",
  });

  const entry = getEntry(dataContext);
  const productId = entry?.options?.[0]?.product?.id ?? entry?.product?.id;
  const customData = entry?.customData;
  const basketAlias = ((customData?.["SimAlias"] as string) || "").trim();
  const userHasEditedRef = useRef(false);
  const lastPersistedAliasRef = useRef(basketAlias);

  const resolveDefaultAlias = () => {
    const aliasKey = getDefaultAliasKey(productId);

    return aliasKey ? t(aliasKey as any) : "";
  };

  const {
    register,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<AliasForm>({
    defaultValues: { alias: basketAlias || resolveDefaultAlias() },
  });

  const { mutate: setDevice } = useSetDeviceByUuid({
    mutation: {
      onSuccess: (data) => {
        setDataContext((prev) => ({
          ...prev,
          basket: withPreservedSimAlias(prev.basket, data),
        }));
      },
    },
  });

  const { mutate: setEntryCustomData } = useSetEntryCustomData({
    mutation: {
      onSuccess: (data) => {
        setDataContext((prev) => ({
          ...prev,
          basket: withPreservedSimAlias(prev.basket, data),
        }));
      },
    },
  });

  const persistSimAlias = (alias: string) => {
    if (!dataContext.basket?.id || !entry?.uuid) {
      return;
    }

    // Optimistic local update so checkout submit validation sees the alias
    // immediately, without waiting for the API mutation to round-trip.
    setDataContext((prev) => {
      const nextBasket = withSimAlias(prev.basket, alias);

      if (!nextBasket) {
        return prev;
      }

      return {
        ...prev,
        basket: nextBasket,
      };
    });

    setEntryCustomData({
      basketId: dataContext.basket.id,
      uuid: entry.uuid,
      data: {
        SimAlias: alias,
      },
    });
    lastPersistedAliasRef.current = alias;
  };

  useEffect(() => {
    if (dataContext.basket?.id && entry?.uuid && productId) {
      const { id: deviceId } = getDeviceForProduct(productId);
      const deviceMismatch = entry.device?.id !== deviceId;

      // Always correct a missing or wrong device (e.g. leftover Flat Home router
      // on a Flat 10 entry) — wrong device can make precharged pricing non-zero.
      if (!entry.device || deviceMismatch) {
        setDevice({
          basketId: dataContext.basket.id,
          uuid: entry.uuid,
          deviceId,
        });
      }

      // Never overwrite a name the user is editing / has already changed.
      if (userHasEditedRef.current) {
        return;
      }

      if (!basketAlias || deviceMismatch) {
        const defaultAliasTranslated = resolveDefaultAlias();

        if (defaultAliasTranslated) {
          setValue("alias", defaultAliasTranslated);
          persistSimAlias(defaultAliasTranslated);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.uuid, dataContext.basket?.id, productId, entry?.device?.id]);

  // Keep local input in sync if the basket already has an alias (e.g. Flat Home
  // prefill from SimVerification) that arrives after first mount.
  useEffect(() => {
    if (userHasEditedRef.current) {
      return;
    }

    if (basketAlias) {
      setValue("alias", basketAlias);
    }
  }, [basketAlias, setValue]);

  useEffect(() => {
    if (showErrors) {
      trigger("alias");
    }
  }, [showErrors, trigger]);

  const handleAliasChange = (e: ChangeEvent<HTMLInputElement>) => {
    userHasEditedRef.current = true;
    const trimmed = e.target.value.trim();

    if (!isValidSimAlias(trimmed)) {
      return;
    }

    setDataContext((prev) => {
      const nextBasket = withSimAlias(prev.basket, trimmed);

      if (!nextBasket) {
        return prev;
      }

      return {
        ...prev,
        basket: nextBasket,
      };
    });
  };

  const handleAliasBlur = () => {
    const currentAlias = getValues("alias") ?? "";
    const trimmedDeviceName = currentAlias.trim();
    const defaultAlias = resolveDefaultAlias();
    const nextAlias = isValidSimAlias(trimmedDeviceName)
      ? trimmedDeviceName
      : isValidSimAlias(basketAlias)
        ? basketAlias
        : defaultAlias;

    if (nextAlias !== currentAlias) {
      setValue("alias", nextAlias, { shouldValidate: true });
    }

    if (!isValidSimAlias(trimmedDeviceName) && nextAlias === defaultAlias) {
      userHasEditedRef.current = false;
    }

    if (
      dataContext.basket?.id &&
      entry?.uuid &&
      isValidSimAlias(nextAlias) &&
      nextAlias !== lastPersistedAliasRef.current
    ) {
      persistSimAlias(nextAlias);
    }
  };

  return (
    <div className="selected-device">
      <FormField<AliasForm>
        id="checkout-sim-alias"
        name="alias"
        label={t("assignedDevice")}
        errors={errors}
        register={register}
        options={{
          maxLength: SIM_ALIAS_MAX_LENGTH,
          validate: (v: string) =>
            isValidSimAlias(v?.trim()) || t("errorDeviceName"),
          onChange: handleAliasChange,
        }}
        onBlur={handleAliasBlur}
        suppressError={!showErrors}
      />
    </div>
  );
};
